import { useMemo } from "react";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useNotification } from "../context/NotificationContext";

/**
 * Role ranks matching the backend user_online.py
 */
export const ROLE_RANK = {
    staff: 1,
    admin: 2,
    super_admin: 3,
    system_super_admin: 4, // Derived from is_system_default
};

export function useAdminPermissions() {
    const { admin } = useAdminAuth();
    const { showNotification } = useNotification();

    const currentRank = useMemo(() => {
        if (!admin) return 0;

        // System Super Admin is rank 4
        if (admin.is_system_default) return ROLE_RANK.system_super_admin;

        return ROLE_RANK[admin.role] || 0;
    }, [admin]);

    /**
     * Returns true if the current admin meets the minimum required role.
     */
    const hasPermission = (minRole) => {
        const minRank = ROLE_RANK[minRole] || 99;
        return currentRank >= minRank;
    };

    /**
     * Guard function for click handlers. 
     * Returns true if permitted, or shows a notification and returns false if denied.
     */
    const checkPermission = (minRole, actionName = "this action") => {
        if (hasPermission(minRole)) return true;

        showNotification({
            type: "failed",
            title: "Permission Denied",
            message: `You do not have the required permissions to ${actionName}.`,
            duration: 5000,
        });

        return false;
    };

    return {
        rank: currentRank,
        role: admin?.role,
        isSystemAdmin: !!admin?.is_system_default,
        hasPermission,
        checkPermission,
    };
}
